import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxes, faEdit, faTrash, faEye, faLayerGroup } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import { PERMISSIONS } from '../../utils/constants';
import DataTable from '../common/DataTable';
import { useCoilSettings } from '../../context/CoilSettingsContext';
import BulkEditWarehouseRows from './BulkEditWarehouseRows';
import { useTranslation } from 'react-i18next';


const WarehouseInventoryTable = ({
  inventoryItems = [],
  loading = false,
  keyField = 'id',
  onViewInventory,
  onEditInventory,
  onDeleteInventory,
  onAdjustInventory,
  onOpenCoilLedger,
  bulkEditMode = false,
  expandedProducts = null,
  onToggleExpand,
  bulkEdits = {},
  bulkErrors = {},
  onBulkCellChange,
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess, checkPermission } = useRole();

  // Moved inside the component so it can access the `t` translation function
  // (module-scope helpers cannot call the react-i18next hook).
  const formatQuantity = useCallback((value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return t('common.symbols.emDash');
    const num = parseFloat(value);
    return `${num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  }, [t]);
  // Editing inventory requires manage access — roles with only VIEW_INVENTORY
  // (Sales Exec, Delivery Supervisor) see a read-only table.
  const canManageInventory = checkPermission(PERMISSIONS.MANAGE_INVENTORY);
  const { coilTrackingEnabled } = useCoilSettings();

  const columns = useMemo(
    () => [
      {
        id: 'product_name',
        label: t('warehouseInventory.table.columns.product_name'),
        sortable: true,
        // Rows are paginated server-side on lower(product name), so this page holds a
        // slice of that ordering. Compare the same way the DB does — plain text, no
        // `numeric` option — or the visible order would disagree with where the page
        // boundaries were cut ("ANGLE 100" vs "ANGLE 25").
        sortCompare: (a, b, direction) => {
          const left = (a?.product_name ?? '').toLowerCase();
          const right = (b?.product_name ?? '').toLowerCase();
          const result = left.localeCompare(right, undefined, { sensitivity: 'base' });
          return direction === 'asc' ? result : -result;
        },
        renderCell: (item) => (
          <div>
            <div className="font-medium flex items-center gap-2" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              <span>{item?.product_name ?? t('common.symbols.emDash')}</span>
              {/* Stock for this size is a PIECE COUNT, not kg — the badge keeps the
                  numbers in the row from being read as weight. */}
              {item?.track_by_pieces && (
                <span
                  className="text-[10px] font-semibold px-1.5 py-0.5 rounded"
                  style={{
                    color: UI_COLORS.STATUS.INFO,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.12),
                  }}
                >
                  {t('pieces.productSize.trackByPieces.badge')}
                </span>
              )}
            </div>
            {item?.product_details?.product_size && (
              <div className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {item.product_details.product_size}
              </div>
            )}
          </div>
        ),
        skeleton: <div className="w-40 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'total_stock_all_warehouses',
        label: t('warehouseInventory.table.columns.total_stock_all_warehouses'),
        sortable: true,
        align: 'right',
        sortAccessor: (item) => parseFloat(item?.total_stock_all_warehouses || 0),
        renderCell: (item) => (
          <div className="text-right">
            <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {formatQuantity(item?.total_stock_all_warehouses)}
            </span>
          </div>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded ml-auto" />,
      },
      {
        id: 'total_pending_all_warehouses',
        label: t('warehouseInventory.table.columns.total_pending_all_warehouses'),
        sortable: true,
        align: 'right',
        sortAccessor: (item) => parseFloat(item?.total_pending_all_warehouses || 0),
        renderCell: (item) => (
          <div className="text-right">
            <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {formatQuantity(item?.total_pending_all_warehouses)}
            </span>
          </div>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded ml-auto" />,
      },
      {
        id: 'total_available_all_warehouses',
        label: t('warehouseInventory.table.columns.total_available_all_warehouses'),
        sortable: true,
        align: 'right',
        sortAccessor: (item) => parseFloat(item?.total_available_all_warehouses || 0),
        renderCell: (item) => (
          <div className="text-right">
            <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {formatQuantity(item?.total_available_all_warehouses)}
            </span>
          </div>
        ),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded ml-auto" />,
      },
      {
        id: 'warehouse_count',
        label: t('warehouseInventory.table.columns.warehouse_count'),
        sortable: true,
        align: 'right',
        sortAccessor: (item) => Number(item?.warehouse_count || 0),
        renderCell: (item) => (
          <div className="text-right">
            <span className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
              {item?.warehouse_count ?? t('common.symbols.emDash')}
            </span>
          </div>
        ),
        skeleton: <div className="w-12 h-4 bg-gray-300 rounded ml-auto" />,
      },
      {
        id: 'actions',
        label: t('warehouseInventory.table.columns.actions'),
        align: 'center',
        justify: 'center',
        alignItems: 'center',
        sortable: false,
        renderCell: (item) => (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onViewInventory?.(item.product_size_id)}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
              style={{
                color: UI_COLORS.BUTTONS.PRIMARY.text,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.08),
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.BUTTONS.PRIMARY.background,
                  0.12
                );
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.BUTTONS.PRIMARY.background,
                  0.08
                );
              }}
              title={t('warehouseInventory.table.aria.viewInventory')}
            >
              <FontAwesomeIcon icon={faEye} className="text-sm" />
            </button>

            {/* Coil ledger. Coil-tracked stock starts empty and is entered by hand, so
                this is where coils first come into existence — without it the only way
                to create one is a purchase receipt. Shown only for coil-tracked SKUs. */}
            {coilTrackingEnabled && item.coil_tracked && canManageInventory && (
              <button
                onClick={() => onOpenCoilLedger?.(item)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.INFO,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                }}
                title={t('coils.ledger.title')}
              >
                <FontAwesomeIcon icon={faLayerGroup} className="text-sm" />
              </button>
            )}

            {canManageInventory && checkEditAccess(item.created_at || item.date) && (
              <button
                onClick={() => onEditInventory?.(item.product_size_id ?? item.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.WARNING,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.WARNING,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.WARNING,
                    0.1
                  );
                }}
                title={t('warehouseInventory.table.aria.editInventory')}
              >
                <FontAwesomeIcon icon={faEdit} className="text-sm" />
              </button>
            )}

            {checkDeleteAccess() && (
              <button
                onClick={() => onDeleteInventory?.(item.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.ERROR,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.ERROR,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.ERROR,
                    0.1
                  );
                }}
                title={t('warehouseInventory.table.aria.deleteInventory')}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}
          </div>
        ),
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="w-8 h-8 bg-gray-300 rounded-lg" />
            ))}
          </div>
        ),
      },
    ],
    [onDeleteInventory, onEditInventory, onViewInventory, onOpenCoilLedger, checkDeleteAccess, checkEditAccess, canManageInventory, coilTrackingEnabled, formatQuantity, t]
  );

  const getRowProps = useCallback(
    () => ({
      className: 'hover:bg-opacity-50 transition-colors',
      style: {
        backgroundColor: 'transparent',
      },
    }),
    []
  );

  const emptyState = useMemo(
    () => ({
      icon: faBoxes,
      title: t('warehouseInventory.table.emptyState.title'),
      message: t('warehouseInventory.table.emptyState.message'),
    }),
    [t]
  );

  const expandable = useMemo(() => {
    if (!bulkEditMode) return null;
    return {
      isRowExpanded: (row) => {
        if (!expandedProducts) return false;
        const key = row?.product_size_id ?? row?.id;
        if (expandedProducts instanceof Set) return expandedProducts.has(key);
        return !!expandedProducts?.[key];
      },
      onToggle: (row) => {
        const key = row?.product_size_id ?? row?.id;
        onToggleExpand?.(key, row);
      },
      renderExpanded: (row) => {
        const key = row?.product_size_id ?? row?.id;
        return (
          <BulkEditWarehouseRows
            product={row}
            edits={bulkEdits?.[key] || {}}
            errors={bulkErrors?.[key] || {}}
            onCellChange={(warehouseId, field, value) => onBulkCellChange?.(key, warehouseId, field, value, row)}
          />
        );
      },
    };
  }, [bulkEditMode, expandedProducts, bulkEdits, bulkErrors, onToggleExpand, onBulkCellChange]);

  return (
    <DataTable
      columns={columns}
      data={Array.isArray(inventoryItems) ? inventoryItems : []}
      loading={loading}
      keyField={keyField}
      enablePagination={false}
      defaultSort={{ field: 'product_name', direction: 'asc' }}
      getRowProps={getRowProps}
      emptyState={emptyState}
      expandable={expandable}
    />
  );
};

export default WarehouseInventoryTable;